import React from 'react';
import { useTranslation } from 'react-i18next';
import PageHeader from '../../../shared/ui/page-header/page-header.jsx';
import FloatingAlerts from '../../../shared/ui/floating-alerts/floating-alerts.jsx';

function TenantBackupsPageView({
  backups,
  runs,
  isLoading,
  isActionLoading,
  error,
  success,
  canRestore,
  restoreState,
  refresh,
  downloadBackup,
  downloadBackupFiles,
  openRestore,
  closeRestore,
  setRestoreConfirmDbName,
  submitRestore,
}) {
  const { t } = useTranslation();

  return (
    <>
      <FloatingAlerts error={error} success={success} />
      <PageHeader title={t('tenantBackups.title')} subtitle={t('tenantBackups.subtitle')} />

      <div className="card mb-4">
        <div className="card-body">
          <div className="d-flex align-items-center justify-content-between mb-3 gap-3">
            <div>
              <h5 className="mb-1">{t('tenantBackups.availableBackups')}</h5>
              <p className="text-muted mb-0">{t('tenantBackups.availableBackupsSubtitle')}</p>
            </div>
            <button type="button" className="btn btn-sm btn-outline-primary" onClick={refresh} disabled={isLoading || isActionLoading}>
              <i className="ti ti-refresh me-2" />
              {t('tenantBackups.refresh')}
            </button>
          </div>

          <div className="table-responsive">
            <table className="table search-table align-middle mb-0">
              <thead>
                <tr>
                  <th>{t('tenantBackups.colBackup')}</th>
                  <th>{t('tenantBackups.colKind')}</th>
                  <th>{t('tenantBackups.colSize')}</th>
                  <th>{t('tenantBackups.colModified')}</th>
                  <th className="text-end">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={5} className="text-muted">{t('common.loading')}</td>
                  </tr>
                ) : backups.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-muted">{t('tenantBackups.noBackups')}</td>
                  </tr>
                ) : backups.map((backup) => (
                  <tr key={backup.objectKey}>
                    <td>
                      <div className="fw-semibold">{backup.displayName}</div>
                      <div className="text-muted small">{backup.lastModifiedLabel}</div>
                    </td>
                    <td>
                      <span className={`badge ${backup.kind === 'pre_restore' ? 'bg-info text-dark' : 'bg-secondary'}`}>
                        {t(backup.kind === 'pre_restore' ? 'tenantBackups.kindPreRestore' : 'tenantBackups.kindScheduled')}
                      </span>
                    </td>
                    <td>{backup.sizeLabel}</td>
                    <td>{backup.lastModifiedLabel}</td>
                    <td className="text-end text-nowrap">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => downloadBackup(backup)}
                        disabled={isActionLoading}
                      >
                        <i className="ti ti-download me-1" />
                        {t('tenantBackups.downloadDatabase')}
                      </button>
                      {backup.canDownloadFiles && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary ms-2"
                          onClick={() => downloadBackupFiles(backup)}
                          disabled={isActionLoading}
                        >
                          <i className="ti ti-file-zip me-1" />
                          {t('tenantBackups.downloadFiles')}
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger ms-2"
                        onClick={() => openRestore(backup)}
                        disabled={isActionLoading || !canRestore}
                      >
                        <i className="ti ti-restore me-1" />
                        {t('tenantBackups.restore')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div>
              <h5 className="mb-1">{t('tenantBackups.recentRuns')}</h5>
              <p className="text-muted mb-0">{t('tenantBackups.recentRunsSubtitle')}</p>
            </div>
          </div>
          <div className="table-responsive">
            <table className="table search-table align-middle mb-0">
              <thead>
                <tr>
                  <th>{t('tenantBackups.colRun')}</th>
                  <th>{t('tenantBackups.colOperation')}</th>
                  <th>{t('tenantBackups.colStatus')}</th>
                  <th>{t('tenantBackups.colRequestedAt')}</th>
                  <th>{t('tenantBackups.colFinishedAt')}</th>
                  <th>{t('tenantBackups.colRequestedBy')}</th>
                  <th>{t('tenantBackups.colObject')}</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={7} className="text-muted">{t('common.loading')}</td>
                  </tr>
                ) : runs.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-muted">{t('tenantBackups.noRuns')}</td>
                  </tr>
                ) : runs.map((run) => (
                  <tr key={run.runId}>
                    <td className="font-monospace small">{run.runId}</td>
                    <td>{run.operationLabel}</td>
                    <td><span className={`badge ${run.statusBadgeClass}`}>{run.status}</span></td>
                    <td>{run.requestedAtLabel}</td>
                    <td>{run.finishedAtLabel}</td>
                    <td>{run.requestedByLabel}</td>
                    <td className="text-break small font-monospace">
                      {run.objectLabel}
                      {run.preRestoreBackupLabel ? (
                        <div className="text-muted mt-1">
                          {t('tenantBackups.preRestoreBackup')}: {run.preRestoreBackupLabel}
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {restoreState.isOpen && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t('tenantBackups.confirmRestoreTitle')}</h5>
                <button type="button" className="btn-close" onClick={closeRestore} disabled={isActionLoading} />
              </div>
              <div className="modal-body">
                <p className="text-muted">{t('tenantBackups.confirmRestoreBody')}</p>
                <div className="alert alert-warning py-2">
                  <i className="ti ti-alert-triangle me-2" />
                  {t('tenantBackups.restoreWarning')}
                </div>
                <div className="mb-3">
                  <label className="form-label" htmlFor="tenant-restore-db-name">{t('tenantBackups.confirmDbNameLabel')}</label>
                  <input
                    id="tenant-restore-db-name"
                    type="text"
                    className="form-control"
                    value={restoreState.confirmDbName}
                    onChange={(event) => setRestoreConfirmDbName(event.target.value)}
                    autoComplete="off"
                    autoFocus
                  />
                  {restoreState.backup?.inferredDbName ? (
                    <div className="form-text">{t('tenantBackups.confirmDbNameHint', { value: restoreState.backup.inferredDbName })}</div>
                  ) : null}
                </div>
                {restoreState.error ? <div className="alert alert-danger py-2">{restoreState.error}</div> : null}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeRestore} disabled={isActionLoading}>
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={submitRestore}
                  disabled={isActionLoading || !restoreState.confirmDbName}
                >
                  {isActionLoading ? t('tenantBackups.queuingRestore') : t('tenantBackups.queueRestore')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default TenantBackupsPageView;
